import { useMemo, useState } from "react";
import classNames from "classnames";

import { ChevronDownIcon, TimerIcon } from "../../AscIcons";

import styles from "./AscActivitiesModuleCard.module.scss";
import AscAdminDropdown from "../../AscAdminDropdown/AscAdminDropdown";
import AscButton from "../../AscButton/AscButton";
import AscProgressBar from "../../AscProgressBar/AscProgressBar";
import AscParagraph from "../../AscTypography/AscParagraph/AscParagraph";

export interface AscActivitiesModuleCardProps {
  className?: string;
  moduleNum: number;
  title: string;
  data: Record<
    string,
    {
      estimatedTime: number;
    }
  > & {
    activity?: {
      activities?: {
        id: string;
        type: string;
        estimatedTime: number;
      }[];
    };
  };
  onEdit?: () => void;
  onDelete?: () => void;
}

export default function AscActivitiesModuleCard({
  className,
  moduleNum,
  title,
  data,
  onEdit,
  onDelete,
}: AscActivitiesModuleCardProps) {
  const [showContent, setShowContent] = useState<boolean>(false);

  const items = useMemo(() => {
    const { activity, ...rest } = data || {};

    const items = Object.keys(rest).map((itemKey) => ({
      name: itemKey,
      estimatedTime: rest?.[itemKey]?.estimatedTime || 0,
    }));

    items.splice(
      2,
      0,
      ...(activity?.activities
        ?.sort((a, b) => {
          if (a.type === "assessment" && b.type !== "assessment") {
            return 1; // "assessment" activities should come last
          } else if (a.type !== "assessment" && b.type === "assessment") {
            return -1; // "assessment" activities should come last
          } else {
            return 0;
          }
        })
        ?.map((item) => ({
          name: item.type,
          estimatedTime: item.estimatedTime || 0,
        })) || [])
    );

    return items;
  }, [data]);

  const handelContent = () => {
    setShowContent(!showContent);
  };

  const timeSum = items
    .map((item) => item?.estimatedTime)
    .reduce((accumulator: number, value: number) => {
      return value ? accumulator + value : accumulator;
    }, 0);

  return (
    <div
      data-testid="asc-activities-module-card"
      className={classNames(styles.ground, className)}
    >
      <div className={styles.topSection}>
        <div className={styles.leftGround}>
          <TimerIcon />
          <div className={styles.text}>{timeSum} Minutes</div>
        </div>

        <div className={styles.middleGround}>
          <div className={styles.title}>
            {" "}
            Module {moduleNum}: {title}
          </div>
          <AscButton
            onClick={handelContent}
            className={classNames(
              showContent ? styles.rotateButton : styles.button
            )}
            icon={<ChevronDownIcon className={styles.icon} />}
          />
        </div>

        <div className={styles.rightGround}>
          <AscAdminDropdown alignment="right" className={styles.dropdown}>
            <AscAdminDropdown.Option onClick={onEdit}>
              Edit
            </AscAdminDropdown.Option>
            <AscAdminDropdown.Option onClick={() => onDelete?.()}>
              Delete
            </AscAdminDropdown.Option>
          </AscAdminDropdown>
        </div>
      </div>

      <div
        className={classNames(
          styles.bottomSection,
          showContent === true && styles.showBottomSection
        )}
      >
        {items.map((item) => (
          <div key={item?.name} className={styles.bottomContent}>
            <div className={styles.timeGround}>
              <TimerIcon className={styles.icon} />
              <AscParagraph className={styles.text}>
                {item?.estimatedTime !== 0 && item?.estimatedTime !== null
                  ? `${item?.estimatedTime} Minutes`
                  : "-"}
              </AscParagraph>
            </div>
            <div className={styles.titleGround}>
              <AscParagraph className={styles.title}>{item?.name}</AscParagraph>
            </div>
          </div>
        ))}

        <div>
          <AscProgressBar
            variant="Admin"
            className={styles.progressBar}
            type="vertical-milestone"
            value={items?.length}
            max={items?.length}
          />
        </div>
      </div>
    </div>
  );
}
